/**
 * PaymentsReturn — landing page after Stripe Checkout success redirect.
 * Reads ?session_id, polls /api/payments/checkout/status/:id every 2s
 * up to 5 tries. On `paid` shows success + reflects new tier. On `expired`
 * shows retry CTA. Idempotent on backend so polling is safe.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_POLLS = 6;
const POLL_INTERVAL_MS = 2000;

const PaymentsReturn = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const isDonation = params.get('donation') === '1';
  const isFounder = params.get('kind') === 'founder';
  const [state, setState] = useState('polling'); // polling | granted | expired | error
  const [pollCount, setPollCount] = useState(0);
  const [tx, setTx] = useState(null);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState('error');
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      const d = res.data || {};
      setTx(d.transaction);
      if (d.tier_granted) {
        setState('granted');
        return;
      }
      if (d.status === 'expired') {
        setState('expired');
        return;
      }
      setPollCount((c) => c + 1);
    } catch (e) {
      setPollCount((c) => c + 1);
    }
  }, [sessionId]);

  useEffect(() => {
    if (state !== 'polling') return;
    if (pollCount >= MAX_POLLS) {
      setState('error');
      return;
    }
    const t = setTimeout(poll, pollCount === 0 ? 0 : POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [pollCount, state, poll]);

  return (
    <div className="min-h-screen bg-transparent text-enf-text-primary px-4 py-16" data-testid="payments-return-page">
      <div className="max-w-md mx-auto text-center">
        {state === 'polling' && (
          <>
            <Loader2 className="w-12 h-12 text-enf-accent-fg animate-spin mx-auto mb-4" />
            <h1 className="font-display text-3xl tracking-wide mb-2">Confirming your subscription…</h1>
            <p className="text-enf-text-secondary text-sm">Stripe just told us you're done. We're activating your access.</p>
          </>
        )}

        {state === 'granted' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            {isDonation || tx?.kind === 'donation' ? (
              <>
                <h1 className="font-display text-3xl tracking-wide mb-2">Thank you</h1>
                <p className="text-enf-text-secondary text-sm mb-2">
                  Your ${tx?.amount?.toFixed(2)} contribution to IronLimb has been received.
                </p>
                {tx?.donation_allocation && (
                  <p className="text-xs text-enf-text-muted mb-6">
                    Allocated to: <span className="text-enf-text-secondary">{tx.donation_allocation}</span>
                  </p>
                )}
                <Link
                  to="/impact"
                  data-testid="payments-return-impact"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary transition-colors"
                >
                  See your impact <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            ) : isFounder || tx?.kind === 'founder_lifetime' ? (
              <>
                <h1 className="font-display text-3xl tracking-wide mb-2">Welcome, Founder.</h1>
                <p className="text-enf-text-secondary text-sm mb-2">
                  Lifetime Enferno access is yours. Numbered tee + sticker pack ship at launch — keep an eye on your inbox.
                </p>
                <p className="text-xs text-enf-text-muted mb-6">
                  Backs Enferno + the IronLimb Foundation directly. Thank you.
                </p>
                <Link
                  to="/progress"
                  data-testid="payments-return-progress"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-amber-600 hover:bg-amber-500 text-zinc-950 transition-colors"
                >
                  Open the app <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <>
                <h1 className="font-display text-3xl tracking-wide mb-2">You're fully Envolved.</h1>
                <p className="text-enf-text-secondary text-sm mb-2">Welcome to ENFERNO!</p>
                {tx?.plan_id && <p className="text-xs text-enf-text-muted mb-6">Plan: {tx.plan_id}</p>}
                <Link
                  to="/progress"
                  data-testid="payments-return-progress"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary transition-colors"
                >
                  Open the app <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </>
        )}

        {state === 'expired' && (
          <>
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="font-display text-3xl tracking-wide mb-2">Checkout expired</h1>
            <p className="text-enf-text-secondary text-sm mb-6">No charge was made. Pick a plan again to retry.</p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-enf-surface-mid hover:bg-enf-surface-elevated text-enf-text-primary transition-colors"
            >
              Back to pricing
            </Link>
          </>
        )}

        {state === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="font-display text-3xl tracking-wide mb-2">Hold tight</h1>
            <p className="text-enf-text-secondary text-sm mb-2">
              We're still waiting for Stripe to confirm your payment. This usually clears up within a minute — refresh in a moment or check your Profile to see if your access has been granted.
            </p>
            <p className="text-xs text-enf-text-muted mb-6">
              If this page persists, email <a href="mailto:support@enferno.app" className="text-enf-accent-fg underline">support@enferno.app</a> with this session ID:
            </p>
            {sessionId && <code className="block text-xs text-enf-text-muted bg-enf-surface-deep p-2 rounded mb-6 break-all">{sessionId}</code>}
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-enf-surface-mid hover:bg-enf-surface-elevated text-enf-text-primary transition-colors"
            >
              Back to pricing
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentsReturn;
